import { API_ENDPOINTS, httpClientInstance } from "@/api"
import type { BillingCostSummary, CostSyncResponse, SyncAccountResult } from "@/types"
import { create } from "zustand"

/**
 * Real AWS cost data for the Dashboard (FREETIER-453): reads the stored summary (database only) and runs the
 * sync (about $0.02 per run). Java analogue: a singleton @Service holding the dashboard's cost state.
 */

/** A sync can take tens of seconds (1 STS call + 2 Cost Explorer requests); the shared client default is 30 s. */
export const SYNC_TIMEOUT_MS = 120_000

export const GENERIC_REFRESH_FAILURE = "We couldn't refresh your AWS cost data. Please try again in a few minutes."
export const GENERIC_LOAD_FAILURE = "We couldn't load your AWS cost data. Please try again in a few minutes."
export const FORBIDDEN_REFRESH_FAILURE = "Your account can't refresh cost data."
export const ALREADY_UP_TO_DATE = "Already up to date"
export const REFRESHED_MOMENT_AGO = "Refreshed a moment ago. Try again in a minute."

export interface CostBanner {
  /** Backend text (message plus guidance) or one of the generic texts above. */
  text: string
  /** When older numbers are still shown: the last successful sync time (ISO), else null. */
  showingFiguresFrom: string | null
}

interface CostReportingData {
  summary: BillingCostSummary | null
  summaryLoading: boolean
  summaryLoaded: boolean
  summaryError: string | null
  syncing: boolean
  syncFailure: string | null
  syncNotice: string | null
  autoSyncAttempted: boolean
}

interface CostReportingState extends CostReportingData {
  loadSummary: () => Promise<BillingCostSummary | null>
  autoSyncIfPending: () => Promise<void>
  refresh: () => Promise<void>
  reset: () => void
}

const initialState: CostReportingData = {
  summary: null,
  summaryLoading: false,
  summaryLoaded: false,
  summaryError: null,
  syncing: false,
  syncFailure: null,
  syncNotice: null,
  autoSyncAttempted: false,
}

// ==================== Wording from response bodies ====================

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null
const asText = (value: unknown): string | null =>
  typeof value === "string" && value.trim() !== "" ? value.trim() : null
const joinText = (...parts: Array<string | null>) => parts.filter((part) => part !== null).join(" ")

const accountErrorText = (account: Record<string, unknown>): string | null => {
  if (!isRecord(account.error)) return null
  const text = joinText(asText(account.error.message), asText(account.error.action))
  if (text === "") return null
  const id = asText(account.aws_account_id)
  return id ? `Account ${id}: ${text}` : text
}

/** Message plus guidance from a 409, a 502 or a completed_with_errors body; null when nothing usable is there. */
const failureText = (body: unknown): string | null => {
  if (!isRecord(body)) return null
  const lines: string[] = []
  const topError = body.error
  if (isRecord(topError)) {
    const text = joinText(asText(topError.message), asText(topError.action))
    if (text !== "") lines.push(text)
  }
  if (Array.isArray(body.accounts)) {
    for (const account of body.accounts) {
      const text = isRecord(account) ? accountErrorText(account) : null
      if (text) lines.push(text)
    }
  }
  // Only the backend's own error details are trusted. A JSON body with just a "message" (a gateway, a proxy, a
  // future middleware) is not shown: the caller falls back to the generic sentence.
  if (lines.length === 0) return null
  const lead = isRecord(topError) ? null : asText(body.message)
  return joinText(lead, ...lines)
}

/** Never returns raw HTTP, axios or AWS text: either text from the backend's body or a generic sentence. */
export const describeSyncFailure = (error: unknown): string => {
  const response = isRecord(error) && isRecord(error.response) ? error.response : null
  if (!response) return GENERIC_REFRESH_FAILURE
  if (response.status === 403) return FORBIDDEN_REFRESH_FAILURE
  if (response.status === 409 || response.status === 502) {
    return failureText(response.data) ?? GENERIC_REFRESH_FAILURE
  }
  return GENERIC_REFRESH_FAILURE
}

const interpretSyncResult = (result: CostSyncResponse): Pick<CostReportingData, "syncFailure" | "syncNotice"> => {
  const accounts: SyncAccountResult[] = Array.isArray(result.accounts) ? result.accounts : []
  if (result.status === "completed_with_errors") {
    return { syncFailure: failureText(result) ?? GENERIC_REFRESH_FAILURE, syncNotice: null }
  }
  if (result.status === "skipped") {
    const reasons = accounts.map((account) => account.reason)
    if (reasons.includes("inactive")) {
      return { syncFailure: asText(result.message) ?? GENERIC_REFRESH_FAILURE, syncNotice: null }
    }
    if (reasons.includes("rate_limited")) return { syncFailure: null, syncNotice: REFRESHED_MOMENT_AGO }
    return { syncFailure: null, syncNotice: ALREADY_UP_TO_DATE }
  }
  return { syncFailure: null, syncNotice: null }
}

// ==================== Selectors ====================

/** What the banner should say, or null. This session's failure wins over the stored error from the summary. */
export const selectCostBanner = (state: Pick<CostReportingData, "summary" | "syncFailure">): CostBanner | null => {
  const { summary, syncFailure } = state
  const showingFiguresFrom = summary?.last_synced_at ?? null
  if (syncFailure) return { text: syncFailure, showingFiguresFrom }
  if (summary?.sync_status === "error") {
    return { text: summary.sync_error?.message ?? GENERIC_REFRESH_FAILURE, showingFiguresFrom }
  }
  return null
}

/** Role-connected customers, and customers with at least one active billing link, use the new data source. */
export const isReportingCustomer = (connectionMethod: string | undefined, summary: BillingCostSummary | null): boolean =>
  connectionMethod === "assume_role" || (summary?.connected_accounts_count ?? 0) > 0

// ==================== Store ====================

export const useCostReportingStore = create<CostReportingState>((set, get) => {
  /** Bumped by reset(): results of requests started before a reset (another user's) are discarded. */
  let generation = 0

  const loadSummary = async (): Promise<BillingCostSummary | null> => {
    const mine = generation
    if (get().summary === null) set({ summaryLoading: true })
    try {
      const summary = await httpClientInstance.get<BillingCostSummary>(API_ENDPOINTS.billing.dashboardSummary)
      if (mine !== generation) return null
      set({ summary, summaryLoading: false, summaryLoaded: true, summaryError: null })
      return summary
    } catch {
      if (mine !== generation) return null
      set({ summaryLoading: false, summaryLoaded: true, summaryError: GENERIC_LOAD_FAILURE })
      return null
    }
  }

  /** Single flight: the check and the set below have no await between them, so two callers cannot both pass. */
  const runSync = async (force: boolean): Promise<void> => {
    if (get().syncing) return
    const mine = generation
    set({ syncing: true, syncNotice: null })
    try {
      const result = await httpClientInstance.post<CostSyncResponse>(
        API_ENDPOINTS.billing.dashboardSync,
        force ? { force_refresh: true } : {},
        { timeout: SYNC_TIMEOUT_MS },
      )
      if (mine !== generation) return
      set(interpretSyncResult(result))
    } catch (error) {
      if (mine !== generation) return
      set({ syncFailure: describeSyncFailure(error), syncNotice: null })
    }
    await loadSummary()
    if (mine === generation) set({ syncing: false })
  }

  return {
    ...initialState,
    loadSummary,

    /** Once per session, only for a never-synced customer; a failed attempt is never retried automatically. */
    autoSyncIfPending: async () => {
      const { summary, syncing, autoSyncAttempted } = get()
      if (autoSyncAttempted || syncing || summary?.sync_status !== "pending") return
      set({ autoSyncAttempted: true })
      await runSync(false)
    },

    /** Forces the sync (past the 15-minute guard) only when the last known status is an error. */
    refresh: async () => {
      await runSync(get().summary?.sync_status === "error")
    },

    reset: () => {
      generation += 1
      set({ ...initialState })
    },
  }
})
